/**
 * A text channel or DM (docs/design/visual-overhaul.md §6.5, §7.9-§7.12,
 * §7.26): the header (title, topic, search field, pins, members), the
 * history with infinite scroll, day and NEW dividers, the new-messages and
 * jump-to-present bars, the composer in the flow with the typing line under
 * it, and the slot beside them (the member list, search results or pins).
 * Also a voice channel's own chat (text-in-voice), embedded.
 *
 * Scrolling (I6, I7, I8): the list is bottom-anchored and has no browser
 * scroll anchoring (`overflow-anchor: none`). One manual anchor, the first
 * message on screen and its offset, is kept on every scroll; whenever the
 * content or the box changes (an older page, a loading row, an image, the
 * composer growing) the list either stays pinned to the end (it was within
 * 80px of it) or puts that message back where it was. Sending scrolls to the
 * end, smoothly when it is less than a screen away and motion is allowed.
 *
 * Keyboard (I10): the message list is one Tab stop with a roving tabindex
 * (↑/↓, Page Up/Down, Home/End move it; Esc goes to the composer); End or
 * Esc in the composer jump to the present; a printable key typed with
 * nothing focused goes to the composer.
 */
import {
  ChannelType,
  Permissions,
  RelationshipType,
  compareSnowflakes,
  isThreadChannelType,
  isVoiceChannelType,
  type Channel,
  type Message,
  type Snowflake,
} from '@neosocial/protocol';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { pushBack } from '@neosocial/ui/back';
import { isCompact, useLayout } from '@neosocial/ui/layout';
import { useT } from '../i18n';
import type { Formatters } from '../i18n/core';
import { api } from '../lib/api';
import { useData, type DataStore } from '../store/data';
import { useDataShallow } from '../store/data';
import {
  ackChannel,
  dropPending,
  loadAround,
  loadHistory,
  loadNewer,
  PAGE,
  reloadHistory,
  retryPending,
  settlePending,
  trimChannel,
  useMsgUi,
  type PendingMessage,
} from '../store/messages';
import { MAX_LOADED } from '../store/reducer';
import { AutoModBlockedNote } from './AutoModAlert';
import { UploadStatus } from './UploadStatus';
import {
  activeGuildId,
  can,
  channelPermissions,
  displayName,
  isDeletedUser,
  presenceOf,
  privateChannelName,
  sortedRoles,
} from '../store/selectors';
import { goHub, useUi } from '../store/ui';
import { useFlag } from '../store/config';
import { NotifyBell, openTitleNotifyMenu } from '../notify/NotifyBell';
import { storage } from '../lib/storage';
import { Avatar, Btn, Icon, IconBtn } from '../ui/kit';
import { Dialog, overlayOpen, toast } from '../ui/overlay';
import { HeaderSearch, PreviewRow, SearchPanel } from './Search';
import { Composer } from './Composer';
import { DmHeaderActions } from './Home';
import { DmCallBand } from './DmCall';
import { MemberList } from './Members';
import { unblock } from './social';
import { MessageRow, type MsgCtx } from './Message';
import { plainText, renderTokens, tokenize, type MentionResolver } from './markdown';
import { openProfile } from './ProfileCard';
import { useVoice } from '../voice/store';
// threads (2026-10-29): the panel beside the channel, the list, a thread's own bits
import { closeThread, closeThreadPanel, useThreadUi } from '../threads/actions';
import { ThreadsHeaderButton } from '../threads/entry';
import { ThreadArchivedNote, ThreadLockedNote, ThreadStart } from '../threads/ThreadBits';
import { ThreadList } from '../threads/ThreadList';
import { ThreadPanel } from '../threads/ThreadPanel';

const GROUP_MS = 7 * 60_000;
/** Within this of the end, the list follows new content (I7). */
const PIN_PX = 80;
/** Further than this from the end, the jump-to-present bar shows (§7.12). */
const FAR_PX = 600;

function sameGroup(a: Message | undefined, b: Message, dayKey: (d: string) => string): boolean {
  if (!a) return false;
  if (b.message_reference || a.type !== 0 || b.type !== 0) return false;
  if (a.author.id !== b.author.id) return false;
  if (dayKey(a.timestamp) !== dayKey(b.timestamp)) return false;
  return Date.parse(b.timestamp) - Date.parse(a.timestamp) < GROUP_MS;
}

type ShapeItem =
  | { k: 'day'; m: Message; label: string }
  | { k: 'new'; m: Message }
  | { k: 'msg'; m: Message; cont: boolean };

/**
 * The history as it is drawn: a day divider before each day's first
 * message, the NEW line before the first message after the read mark
 * (others' only), and whether each message continues its group.
 */
export function listShape(
  list: Message[],
  markId: Snowflake | null,
  meId: Snowflake | undefined,
  fmt: Pick<Formatters, 'day' | 'dayKey'>,
): { items: ShapeItem[]; newCount: number; firstNew: Message | undefined } {
  const items: ShapeItem[] = [];
  let prev: Message | undefined;
  let prevDay = '';
  let newCount = 0;
  let firstNew: Message | undefined;
  list.forEach((m) => {
    // one day key per message (Intl formatting), not four
    const day = fmt.dayKey(m.timestamp);
    if (!prev || prevDay !== day) {
      items.push({ k: 'day', m, label: fmt.day(m.timestamp) });
      prev = undefined;
    }
    if (markId && compareSnowflakes(m.id, markId) > 0 && m.author.id !== meId) {
      newCount++;
      if (!firstNew) {
        firstNew = m;
        items.push({ k: 'new', m });
        prev = undefined;
      }
    }
    // `prev` is set only on the same day: its key is `prevDay`
    const keyOf = (ts: string) => (ts === m.timestamp ? day : prevDay);
    items.push({ k: 'msg', m, cont: sameGroup(prev, m, keyOf) });
    prev = m;
    prevDay = day;
  });
  return { items, newCount, firstNew };
}

/** A guild's channel names, in a stable order (a rename or a new channel changes it). */
function channelNames(d: DataStore, gid: Snowflake | undefined): string[] {
  if (!gid) return NO_NAMES;
  const out: string[] = [];
  for (const c of Object.values(d.channels)) if (c.guild_id === gid) out.push(c.id, c.name ?? '');
  return out;
}
const NO_NAMES: string[] = [];

/**
 * How mentions read (names, roles, channels). Its identity changes only when
 * a name it can return changes: the message rows re-render then, and never
 * because a message arrived or someone's presence changed.
 */
export function useResolver(channel: Channel | undefined): MentionResolver {
  const meId = useData((d) => d.me?.id);
  const gid0 = channel?.guild_id;
  const users = useData((d) => d.users);
  const members = useData((d) => (gid0 ? d.members[gid0] : undefined));
  const roles = useData((d) => (gid0 ? d.guilds[gid0]?.roles : undefined));
  const names = useDataShallow((d) => channelNames(d, gid0));
  return useMemo(() => {
    const gid = channel?.guild_id;
    return {
      meId,
      user: (id) => {
        const d = useData.getState();
        const u = d.users[id];
        if (!u) return null;
        return displayName(u, gid ? d.members[gid]?.[id] : undefined);
      },
      role: (id) => {
        const d = useData.getState();
        const r = gid ? d.guilds[gid]?.roles.find((x) => x.id === id) : undefined;
        return r ? { name: r.name, color: r.color } : null;
      },
      channel: (id) => useData.getState().channels[id]?.name ?? null,
      onUser: (id, el) => openProfile(id, el),
      onChannel: (id) => {
        const c = useData.getState().channels[id];
        // a thread opens beside its channel (Hub routes it)
        if (c && (c.type === ChannelType.GUILD_TEXT || isThreadChannelType(c.type)))
          goHub({ kind: 'channel', channelId: id });
      },
    };
    // the names it reads at call time: a new resolver whenever one of them changes
    // eslint-disable-next-line react-hooks/exhaustive-deps -- users, members, roles, names are change signals
  }, [channel?.guild_id, meId, users, members, roles, names]);
}

/** The typing line under the composer (§6.5): 24 tall, the sentence in the UI's direction. */
function Typing({ channelId, guildId }: { channelId: Snowflake; guildId?: Snowflake }) {
  const { t } = useT();
  const who = useDataShallow((d) => Object.keys(d.typing[channelId] ?? {}));
  const names = useDataShallow((d) =>
    who.map((id) => displayName(d.users[id], guildId ? d.members[guildId]?.[id] : undefined) || id),
  );
  const text = !who.length
    ? ''
    : names.length === 1
      ? t('ns.typing.one', { a: names[0] })
      : names.length === 2
        ? t('ns.typing.two', { a: names[0], b: names[1] })
        : t('ns.typing.many', { n: names.length });
  return (
    <div className="typing" aria-live="polite">
      {who.length ? (
        <span className="typing-pill">
          <span className="dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          {/* "{a} is typing…" in the UI's direction: a <bdi> took it from the name */}
          <span className="typing-t">{text}</span>
        </span>
      ) : null}
    </div>
  );
}

/** "Waiting to send… 4 s": a send refused for speed goes out by itself. */
function WaitNote({ until }: { until: number }) {
  const { t } = useT();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const h = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(h);
  }, []);
  return (
    <div className="ns-pending-wait" role="status">
      <Icon name="clock" size="s" />
      {t('ns.msg.waiting', { n: Math.max(0, Math.ceil((until - now) / 1000)) })}
    </div>
  );
}

function PendingRow({
  p,
  cont,
  resolver,
}: {
  p: PendingMessage;
  cont: boolean;
  resolver: MentionResolver;
}) {
  const { t } = useT();
  const me = useData((d) => d.me);
  const fileCount = p.fileCount ?? p.files.length;
  return (
    <article
      className={'msg mine ns-pending' + (cont ? ' cont' : '')}
      data-state={p.state}
      tabIndex={-1}
    >
      <div className="msg-gut">{cont ? null : <Avatar user={me} size={40} />}</div>
      <div className="msg-body">
        {!cont ? (
          <header className="msg-h">
            <span className="msg-name" dir="auto">
              {me ? me.global_name || me.username : ''}
            </span>
          </header>
        ) : null}
        <div className="msg-text" dir="auto">
          {renderTokens(tokenize(p.content), resolver, t('hub.spoiler'))}
          {fileCount ? (
            <span className="ns-pending-files"> · {t('ns.msg.files', { n: fileCount })}</span>
          ) : null}
        </div>
        {p.state === 'waiting' && p.until ? <WaitNote until={p.until} /> : null}
        {fileCount && p.state === 'sending' ? <UploadStatus p={p} /> : null}
        {p.state === 'failed' && p.automod ? (
          <AutoModBlockedNote p={p} />
        ) : p.state === 'failed' ? (
          <div className="ns-pending-fail" role="alert">
            <Icon name="alert" size="s" />
            {t('ns.msg.failed')}
            <button type="button" onClick={() => retryPending(p.channelId, p.nonce)}>
              {t('ns.msg.retry')}
            </button>
            <button type="button" onClick={() => dropPending(p.channelId, p.nonce)}>
              {t('ns.msg.discard')}
            </button>
          </div>
        ) : null}
      </div>
    </article>
  );
}

/** Pinned messages, docked in the slot (§7.26): rows that open their message. */
function Pins({ channelId, ctx, close }: { channelId: Snowflake; ctx: MsgCtx; close(): void }) {
  const { t } = useT();
  const version = useData((d) => d.pinsVersion[channelId] ?? 0);
  const [state, setState] = useState<{ items: Message[] | null; error: boolean }>({
    items: null,
    error: false,
  });
  useEffect(() => {
    let live = true;
    api.channels
      .pins(channelId)
      .then((r) => live && setState({ items: r.items.map((x) => x.message), error: false }))
      .catch(() => live && setState({ items: null, error: true }));
    return () => {
      live = false;
    };
  }, [channelId, version]);
  return (
    <div className="ns-pins slot-panel" role="region" aria-label={t('ns.pins.title')}>
      <div className="slot-h ns-pop-h">
        <Icon name="pin" size="m" />
        <h2 className="slot-t">{t('ns.pins.title')}</h2>
        {state.items?.length ? <span className="slot-n nx-num">{state.items.length}</span> : null}
        <IconBtn icon="x" label={t('c.close')} onClick={close} />
      </div>
      <div className="ns-pins-list nx-scroll">
        {state.error ? <p className="ns-muted slot-note">{t('ns.pins.error')}</p> : null}
        {!state.items && !state.error ? (
          <p className="ns-muted slot-note">
            <i className="nx-spin" /> {t('ns.loading')}
          </p>
        ) : null}
        {state.items && !state.items.length ? (
          <div className="slot-empty">
            <span className="slot-empty-ic" aria-hidden="true">
              <Icon name="pin" size="xl" />
            </span>
            <p>{t('ns.pins.none')}</p>
          </div>
        ) : null}
        {state.items?.length ? (
          <ul>
            {state.items.map((m) => (
              <PreviewRow
                key={m.id}
                m={m}
                cls="ns-pin"
                onOpen={() => ctx.onJump(m.id)}
                close={close}
              />
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

/** The first message after the read mark when the channel opened (the NEW divider, P7). */
function unreadMark(channelId: Snowflake): Snowflake | null {
  const d = useData.getState();
  const rs = d.readStates[channelId];
  const last = d.channels[channelId]?.last_message_id;
  if (!rs?.last_message_id || !last || compareSnowflakes(last, rs.last_message_id) <= 0)
    return null;
  return rs.last_message_id;
}

const motionOk = () => document.documentElement.dataset.motion !== 'reduce';
/** To the end of the list (scrollTo where the browser has it). */
function scrollEnd(el: HTMLElement, behavior: ScrollBehavior) {
  if (typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight, behavior });
  else el.scrollTop = el.scrollHeight;
}
const NARROW = '(max-width: 1099px)';

/** Below 1100 the slot is a drawer over the chat (§6.10). */
function useNarrow(): boolean {
  const [narrow, setNarrow] = useState(
    () => typeof matchMedia === 'function' && matchMedia(NARROW).matches,
  );
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia(NARROW);
    const on = () => setNarrow(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return narrow;
}

/** What inside a message could take the Tab key (the list keeps them all out of it, I10). */
const FOCUSABLE = ':is(button, a[href], [tabindex="0"])';

/** "Message #a-very-long-channel-nam…": the name cut at 24 characters (I24). */
const cut = (s: string) => ([...s].length > 24 ? [...s].slice(0, 24).join('') + '…' : s);

type Slot = 'members' | 'search' | 'pins' | null;
/** The slot's content: a Slot, or a thread open beside the channel, or its threads list. */
type Panel = Slot | 'thread' | 'threads';

export function ChannelView({
  channelId,
  embedded,
  threadId,
  inThread,
}: {
  channelId: Snowflake;
  embedded?: boolean;
  /** A thread of this channel open in the slot beside it (threads/). */
  threadId?: Snowflake;
  /** This view is a thread's conversation, inside its panel (embedded). */
  inThread?: boolean;
}) {
  const { t, fmt } = useT();
  const channel = useData((d) => d.channels[channelId]);
  const cm = useData((d) => d.messages[channelId]);
  const loading = useMsgUi((s) => s.loading[channelId]);
  const failed = useMsgUi((s) => s.failed[channelId]);
  const pending = useMsgUi((s) => s.pending[channelId]);
  const perms = useDataShallow((d) => channelPermissions(d, channel));
  const showMembersPref = useUi((u) => u.showMembers);
  const narrow = useNarrow();
  const [drawerPref, setDrawerPref] = useState(() => storage.get('members-drawer') === '1');
  // phones and tablets (§12.7 A2, A5): one pane shown (a phone, a tablet in portrait)
  // opens the member list closed in every channel, whatever the wide window's
  // preference; every drawer or pane over the chat is a back-stack layer
  const lay = useLayout();
  const compact = isCompact(lay);
  const touchFrame = lay.layout !== 'desktop';
  const [membersOpen, setMembersOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const resolver = useResolver(channel);
  const scroller = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  /** Within PIN_PX of the end: new content keeps the end in view. */
  const pinned = useRef(true);
  /** The first message on screen and its offset from the scroller's top (the one manual anchor). */
  const anchor = useRef<{ id: string; top: number } | null>(null);
  /** The next layout goes to the end (a send); smooth when that is close. */
  const toEnd = useRef<ScrollBehavior | null>(null);
  const [far, setFar] = useState(false);
  const [newAbove, setNewAbove] = useState(false);
  const isDm = channel?.type === ChannelType.DM || channel?.type === ChannelType.GROUP_DM;
  // a voice or stage channel's own chat: the speaker, never '#'
  const voice = !!channel && isVoiceChannelType(channel.type);
  const title = useData((d) =>
    channel ? (isDm ? privateChannelName(d, channel) : (channel.name ?? '')) : '',
  );
  const dmPeer = useData((d) =>
    channel?.type === ChannelType.DM
      ? channel.recipients?.find((u) => u.id !== d.me?.id)
      : undefined,
  );
  const dmPresence = useData((d) => (dmPeer ? presenceOf(d, dmPeer.id) : null));
  // a DM with someone you blocked: say so, with the way back (§3.2), not a composer that fails
  const dmBlocked = useData(
    (d) => !!dmPeer && d.relationships[dmPeer.id]?.type === RelationshipType.BLOCKED,
  );
  const canEveryone = can(perms, Permissions.MENTION_EVERYONE);
  // without Read Message History the server sends no history (Discord): say so, never "the start"
  const canHistory = isDm || can(perms, Permissions.READ_MESSAGE_HISTORY);
  const [topicOpen, setTopicOpen] = useState(false);
  const [slot, setSlot] = useState<Slot>(null);
  const [query, setQuery] = useState('');

  // the read mark when this channel opened: the NEW divider stays until you leave (Discord);
  // state kept from the render that opened the channel (React's "previous render" pattern)
  const [mark, setMark] = useState<{ ch: Snowflake; id: Snowflake | null }>(() => ({
    ch: channelId,
    id: unreadMark(channelId),
  }));
  let markId = mark.id;
  if (mark.ch !== channelId) {
    markId = unreadMark(channelId);
    setMark({ ch: channelId, id: markId });
  }
  const acked = useData((d) => {
    const rs = d.readStates[channelId]?.last_message_id;
    const last = d.channels[channelId]?.last_message_id;
    return !last || (!!rs && compareSnowflakes(rs, last) >= 0);
  });

  // a new channel in the same view (the voice room's chat): start at its end; this runs
  // before the content's layout effect below, in the same commit
  const shown = useRef(channelId);
  const opened = useRef(false);
  useLayoutEffect(() => {
    if (shown.current === channelId) return;
    shown.current = channelId;
    pinned.current = true;
    anchor.current = null;
    opened.current = false;
  }, [channelId]);

  // the open channel loads again at once when a fresh READY made its copy stale
  const stale = !!cm?.stale;
  useEffect(() => {
    // a channel left while viewing older messages opens at the present again
    // (and so does a stale copy of such a window: what was missed is newer)
    if (useData.getState().messages[channelId]?.hasNewer) void reloadHistory(channelId);
    else void loadHistory(channelId);
  }, [channelId, stale]);

  // history granted again while the channel is open: load it now
  const hadHistory = useRef(canHistory);
  useEffect(() => {
    const before = hadHistory.current;
    hadHistory.current = canHistory;
    if (canHistory && !before) void reloadHistory(channelId);
  }, [canHistory, channelId]);

  // read marks: while the channel is on screen and the window has focus
  const last = channel?.last_message_id;
  useEffect(() => {
    // not while a stale copy is on screen: what was missed is not shown yet
    if (!cm?.loaded || stale) return;
    const markRead = () => {
      // not while viewing older messages: the newest ones are not on screen
      const viewingOlder = useData.getState().messages[channelId]?.hasNewer;
      if (document.hasFocus() && pinned.current && !viewingOlder) ackChannel(channelId);
    };
    markRead();
    window.addEventListener('focus', markRead);
    return () => window.removeEventListener('focus', markRead);
  }, [channelId, last, cm?.loaded, stale]);

  const list = useMemo(() => (cm ? cm.ids.map((id) => cm.byId[id]!).filter(Boolean) : []), [cm]);
  useEffect(() => {
    for (const m of list.slice(-5)) settlePending(m);
  }, [list]);

  // memory: a channel left keeps one page loaded; one read at its end keeps the window
  // (the oldest go once it is a page past it, out of sight above the reader)
  useEffect(() => () => trimChannel(channelId), [channelId]);
  useEffect(() => {
    if (pinned.current && list.length > MAX_LOADED + PAGE && !cm?.hasNewer)
      trimChannel(channelId, MAX_LOADED);
  }, [list, channelId, cm?.hasNewer]);

  /** Scrolls to a message on screen and flashes it; false when it is not rendered. */
  const flash = useCallback((id: Snowflake): boolean => {
    const el = scroller.current?.querySelector<HTMLElement>(`#msg-${id}`);
    if (!el) return false;
    pinned.current = false;
    el.scrollIntoView({ block: 'center', behavior: motionOk() ? 'smooth' : 'auto' });
    el.classList.remove('nx-flash');
    void el.offsetWidth;
    el.classList.add('nx-flash');
    return true;
  }, []);
  /** A jump whose page just loaded: flashed once it is rendered. */
  const flashNext = useRef<Snowflake | null>(null);
  /** The NEW divider while it is drawn. */
  const newLine = useRef<HTMLDivElement | null>(null);
  const viewingOlder = !!cm?.hasNewer;

  /** Reads where the list is: the anchor, pinned, far, and whether NEW is above the screen. */
  const measure = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinned.current = dist < PIN_PX;
    setFar(dist > FAR_PX);
    const top = el.getBoundingClientRect().top;
    anchor.current = null;
    // the first message (or pending one) whose bottom is below the top. The list's
    // children are in order: a binary search reads a handful of boxes per scroll
    // event, not every row above the screen, and no node list is built for it
    const kids = inner.current?.children;
    let lo = 0;
    let hi = kids?.length ?? 0;
    while (kids && lo < hi) {
      const mid = (lo + hi) >> 1;
      if (kids[mid]!.getBoundingClientRect().bottom > top) hi = mid;
      else lo = mid + 1;
    }
    let first: Element | undefined;
    for (let i = lo; kids && i < kids.length; i++) {
      const k = kids[i]!;
      if (k.hasAttribute('data-mid') || k.classList.contains('ns-pending')) {
        first = k;
        break;
      }
    }
    if (first?.id) anchor.current = { id: first.id, top: first.getBoundingClientRect().top - top };
    // the NEW line by its ref: a selector scanned the whole list on every scroll event
    const nd = newLine.current?.isConnected ? newLine.current : null;
    setNewAbove(!!nd && nd.getBoundingClientRect().bottom < top);
  }, []);

  /** Puts the list back where the reader had it: the end, or the anchor at its offset. */
  const settle = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const behavior = toEnd.current;
    if (behavior) {
      toEnd.current = null;
      pinned.current = true;
      scrollEnd(el, behavior);
      return;
    }
    if (pinned.current && !useData.getState().messages[channelId]?.hasNewer) {
      el.scrollTop = el.scrollHeight;
      return;
    }
    const a = anchor.current;
    const node = a ? el.querySelector<HTMLElement>('#' + a.id) : null;
    if (a && node) {
      const now = node.getBoundingClientRect().top - el.getBoundingClientRect().top;
      if (Math.abs(now - a.top) > 0.5) el.scrollTop += now - a.top;
    }
  }, [channelId]);

  // every change of the content: keep the reader's place (I6) or the end (I7)
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const f = flashNext.current;
    if (f && flash(f)) {
      flashNext.current = null;
      return;
    }
    // the first look at a channel with unread messages: the first of them a third down (P7)
    if (!opened.current && cm?.loaded && list.length) {
      opened.current = true;
      const nd = el.querySelector<HTMLElement>('.nx-newline');
      el.scrollTop = el.scrollHeight;
      if (nd) {
        const top = nd.getBoundingClientRect().top - el.getBoundingClientRect().top;
        if (top < 0) {
          el.scrollTop += top - el.clientHeight / 3;
          pinned.current = false;
        }
      }
      measure();
      return;
    }
    settle();
  }, [list, pending, loading, failed, flash, settle, measure, cm?.loaded, markId]);

  // the box or its content changed size (an image loaded, the composer grew, the window):
  // the same rule, without a render
  useEffect(() => {
    const el = scroller.current;
    const box = inner.current;
    if (!el || !box || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => settle());
    ro.observe(el);
    ro.observe(box);
    return () => ro.disconnect();
  }, [settle]);

  const onScroll = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    measure();
    const st = useData.getState().messages[channelId];
    if (pinned.current && document.hasFocus() && !st?.hasNewer) ackChannel(channelId);
    // viewing older messages: the newer pages load as you scroll down
    if (
      st?.hasNewer &&
      st.loaded &&
      el.scrollHeight - el.scrollTop - el.clientHeight < 240 &&
      !useMsgUi.getState().loading[channelId]
    )
      void loadNewer(channelId);
    if (el.scrollTop < 240 && st?.hasMore && st.loaded && !useMsgUi.getState().loading[channelId])
      void loadHistory(channelId, true);
  }, [channelId, measure]);

  /** Back to the newest messages: a reload when older ones are on screen, else a scroll. */
  const toPresent = useCallback(() => {
    const el = scroller.current;
    if (useData.getState().messages[channelId]?.hasNewer) {
      toEnd.current = 'auto';
      pinned.current = true;
      void reloadHistory(channelId);
      return;
    }
    if (!el) return;
    pinned.current = true;
    scrollEnd(el, motionOk() ? 'smooth' : 'auto');
  }, [channelId]);

  /** A send: the end, smoothly when it is less than a screen away (I8). */
  const onSent = useCallback(() => {
    const el = scroller.current;
    const near = !!el && el.scrollHeight - el.scrollTop - el.clientHeight < el.clientHeight;
    toEnd.current = near && motionOk() ? 'smooth' : 'auto';
  }, []);

  // a pin, a reply's quote line or a search result: show that message here
  const onJump = useCallback(
    (id: Snowflake) => useUi.getState().set({ jump: { channelId, messageId: id } }),
    [channelId],
  );
  const jump = useUi((u) => (u.jump?.channelId === channelId ? u.jump.messageId : null));
  useEffect(() => {
    if (!jump || !cm?.loaded || loading) return;
    useUi.getState().set({ jump: null });
    pinned.current = false;
    if (cm.byId[jump]) {
      requestAnimationFrame(() => flash(jump));
      return;
    }
    // not loaded: the page around it, however far back (Discord), with the way back to the present
    void loadAround(channelId, jump).then((ok) => {
      if (!ok) {
        toast(t('um.search.jumpFailed'), { icon: 'history' });
        return;
      }
      flashNext.current = jump;
      requestAnimationFrame(() => {
        if (flashNext.current === jump && flash(jump)) flashNext.current = null;
      });
    });
  }, [jump, cm, loading, channelId, flash, t]);

  // the message list is one Tab stop (I10): only the active message is tabbable, and
  // nothing inside messages is (their buttons stay reachable by pointer and by the keys)
  const active = useRef<string | null>(null);
  /**
   * The whole list once; after that, what changed (chat performance,
   * 2026-11-01: every change anywhere in it, a hover toolbar built or a
   * reaction count, swept every button of every message). `added`: the nodes
   * just put in; `rows`: messages came or went, so the Tab stop is chosen again.
   */
  const rove = useCallback((added?: Element[], rows = true) => {
    const el = scroller.current;
    if (!el) return;
    if (rows) {
      const msgs = [...el.querySelectorAll<HTMLElement>('.msgs-in > [data-mid]')];
      const cur =
        (active.current && msgs.find((x) => x.id === active.current)) || msgs[msgs.length - 1];
      for (const x of msgs) {
        const want = x === cur ? 0 : -1;
        if (x.tabIndex !== want) x.tabIndex = want;
      }
    }
    const off = (b: HTMLElement) => {
      if (b.tabIndex !== -1) b.tabIndex = -1;
    };
    if (!added) {
      for (const b of el.querySelectorAll<HTMLElement>(`.msgs-in > [data-mid] ${FOCUSABLE}`))
        off(b);
      return;
    }
    for (const n of added) {
      const msg = n.closest('.msgs-in > [data-mid]');
      if (!msg) continue;
      if (n !== msg && n.matches(FOCUSABLE)) off(n as HTMLElement);
      for (const b of n.querySelectorAll<HTMLElement>(FOCUSABLE)) off(b);
    }
  }, []);
  // the whole list when its rows change; the observer below takes what appears inside them
  useLayoutEffect(() => rove(), [rove, list, pending, loading]);
  useEffect(() => {
    const el = scroller.current;
    const box = inner.current;
    if (!el || typeof MutationObserver === 'undefined') return;
    const mo = new MutationObserver((recs) => {
      const added: Element[] = [];
      let rows = false;
      for (const r of recs) {
        if (r.target === box) rows = true;
        for (const n of r.addedNodes) if (n instanceof Element) added.push(n);
      }
      if (rows || added.length) rove(added, rows);
    });
    mo.observe(el, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [rove]);
  const focusComposer = () =>
    scroller.current?.closest('.chat')?.querySelector<HTMLElement>('textarea.cmp-in')?.focus();
  const onListKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (!target.matches('[data-mid]')) return;
    const msgs = [
      ...(scroller.current?.querySelectorAll<HTMLElement>('.msgs-in > [data-mid]') ?? []),
    ];
    const i = msgs.indexOf(target);
    let next: HTMLElement | undefined;
    if (e.key === 'ArrowUp') next = msgs[Math.max(0, i - 1)];
    else if (e.key === 'ArrowDown') next = msgs[Math.min(msgs.length - 1, i + 1)];
    else if (e.key === 'PageUp') next = msgs[Math.max(0, i - 10)];
    else if (e.key === 'PageDown') next = msgs[Math.min(msgs.length - 1, i + 10)];
    else if (e.key === 'Home') next = msgs[0];
    else if (e.key === 'End') {
      e.preventDefault();
      toPresent();
      next = msgs[msgs.length - 1];
    } else if (e.key === 'Escape') {
      e.preventDefault();
      focusComposer();
      return;
    } else return;
    e.preventDefault();
    if (!next) return;
    active.current = next.id;
    rove();
    next.focus({ preventScroll: true });
    next.scrollIntoView({ block: 'nearest' });
  };
  // a printable key typed with nothing focused goes to the composer (I10): the channel's,
  // not the one of a thread beside it
  useEffect(() => {
    if (inThread) return;
    const key = (e: globalThis.KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1) return;
      const a = document.activeElement;
      if (a && a !== document.body && !a.matches('.msgs, .msgs-in > [data-mid]')) return;
      if (overlayOpen() || e.isComposing) return;
      // the push-to-talk key stays push-to-talk
      const v = useVoice.getState().prefs;
      if (v.inputMode === 'ptt' && e.code === v.pttKey) return;
      focusComposer();
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [inThread]);
  // Esc or End in the chat's own fields: back to the present when it is off screen
  const onChatKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.defaultPrevented) return;
    const inComposer = (e.target as HTMLElement).matches('textarea.cmp-in');
    if (!inComposer || !(far || viewingOlder)) return;
    const empty = !(e.target as HTMLTextAreaElement).value;
    if (e.key === 'Escape' || (e.key === 'End' && empty && !e.shiftKey)) {
      e.preventDefault();
      toPresent();
    }
  };

  // the operator's flags (admin-dashboard §4.9): off means hidden here, refused by the server
  const reactionsOn = useFlag('reactions');
  const pinsOn = useFlag('pins');
  const uploadsOn = useFlag('image_uploads');
  const searchOn = useFlag('search');
  // The rows read the channel's identity and place only. The channel object
  // changes with every message (its last_message_id): a context built on it
  // re-rendered every row, markdown and all, for each message that arrived
  // (chat performance, 2026-11-01).
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the fields the rows read
  const rowChannel = useMemo(() => channel, [channel?.id, channel?.guild_id, channel?.type]);
  const ctx: MsgCtx | null = useMemo(
    () =>
      rowChannel
        ? {
            channel: rowChannel,
            resolver,
            perms: {
              manage: can(perms, Permissions.MANAGE_MESSAGES),
              // what the server checks for pin and unpin: Pin Messages (not Manage Messages)
              pin: pinsOn && can(perms, Permissions.PIN_MESSAGES),
              react: reactionsOn && can(perms, Permissions.ADD_REACTIONS),
              joinReact: reactionsOn && canHistory,
              send: can(perms, Permissions.SEND_MESSAGES),
            },
            onReply: (m: Message) => setReplyTo(m),
            onJump,
          }
        : null,
    [rowChannel, resolver, perms, onJump, pinsOn, reactionsOn, canHistory],
  );

  // the list's shape (day dividers, the NEW line, grouping) changes only with the
  // messages or the read mark: not on every render of the view
  const meIdNow = useData((d) => d.me?.id);
  const shape = useMemo(() => listShape(list, markId, meIdNow, fmt), [list, markId, meIdNow, fmt]);

  // the header field's query: typing opens the results in the slot (live, as it settles)
  useEffect(() => {
    if (!query.trim()) return;
    const h = setTimeout(() => setSlot('search'), 300);
    return () => clearTimeout(h);
  }, [query]);

  const membersOn =
    !embedded && !isDm && (compact ? membersOpen : narrow ? drawerPref : showMembersPref);
  // threads (2026-10-29): an open thread, else the threads list, takes the slot over the members
  const threadsPanel = useThreadUi((s) =>
    !embedded && s.panel?.channelId === channelId ? s.panel : null,
  );
  const panel: Panel = embedded
    ? null
    : (slot ?? (threadId ? 'thread' : threadsPanel ? 'threads' : membersOn ? 'members' : null));
  /** Off the desktop the slot is a drawer (or a full pane) over the chat: a back-stack layer. */
  const layer: Panel = touchFrame ? panel : null;
  // a thread or the list opening closes search and pins (one thing in the slot);
  // adjusted while rendering, as the NEW mark above (React's "previous render" pattern)
  const threadsOpen = threadId ?? threadsPanel?.view ?? null;
  const [seenOpen, setSeenOpen] = useState(threadsOpen);
  if (seenOpen !== threadsOpen) {
    setSeenOpen(threadsOpen);
    if (threadsOpen && slot) setSlot(null);
  }
  const closeSlot = () => {
    if (slot === 'search') {
      setQuery('');
      // the header field closes with its results (the keyboard goes down)
      if (touchFrame && document.activeElement?.closest('.ch-search'))
        (document.activeElement as HTMLElement).blur();
    }
    setSlot(null);
  };
  /** Closes the layer over the chat: Back, a tap on its scrim. */
  const closeLayer = () => {
    if (slot) closeSlot();
    else if (panel === 'thread') closeThread({ parent_id: channelId });
    else if (panel === 'threads') closeThreadPanel();
    else if (compact) setMembersOpen(false);
    else {
      storage.set('members-drawer', '0');
      setDrawerPref(false);
    }
  };
  const closeLayerRef = useRef(closeLayer);
  useEffect(() => {
    closeLayerRef.current = closeLayer;
  });
  useEffect(() => {
    if (!layer) return;
    return pushBack(() => closeLayerRef.current());
  }, [layer]);

  if (!channel || !ctx) return null;
  const sendable = can(perms, Permissions.SEND_MESSAGES);
  const name = isDm || voice ? title : '#' + title;
  const toggleMembers = () => {
    // the members over an open thread or the threads list: those close, the members show
    if (panel === 'thread' || panel === 'threads') {
      if (panel === 'thread') closeThread({ parent_id: channelId });
      else closeThreadPanel();
      if (compact) setMembersOpen(true);
      else if (narrow) {
        storage.set('members-drawer', '1');
        setDrawerPref(true);
      } else {
        storage.set('members', '1');
        useUi.getState().set({ showMembers: true });
      }
      return;
    }
    if (compact) {
      const v = slot ? true : !membersOpen;
      if (slot) closeSlot();
      setMembersOpen(v);
      return;
    }
    if (slot) setSlot(null);
    if (narrow) {
      const v = !(slot ? false : drawerPref);
      storage.set('members-drawer', v ? '1' : '0');
      setDrawerPref(v);
      return;
    }
    const v = slot ? true : !showMembersPref;
    storage.set('members', v ? '1' : '0');
    useUi.getState().set({ showMembers: v });
  };

  const meId = meIdNow;
  const rows: React.ReactNode[] = [];
  const { newCount, firstNew } = shape;
  for (const it of shape.items) {
    if (it.k === 'day')
      rows.push(
        <div key={'d' + it.m.id} className="hub-day" role="separator">
          {/* Today, Yesterday or the date: never two of them (P38) */}
          <span>{it.label}</span>
        </div>,
      );
    else if (it.k === 'new')
      rows.push(
        <div
          key={'n' + it.m.id}
          ref={newLine}
          className="nx-newline"
          role="separator"
          aria-label={t('chat.new.label')}
        >
          <span>{t('chat.new.tag')}</span>
        </div>,
      );
    else
      rows.push(
        <MessageRow
          key={it.m.id}
          m={it.m}
          cont={it.cont}
          ctx={ctx}
          replying={replyTo?.id === it.m.id}
        />,
      );
  }
  (pending ?? []).forEach((p, i) => {
    const before = i === 0 ? list.at(-1) : undefined;
    const cont =
      i > 0 ||
      (before?.author.id === meId && p.at - Date.parse(before?.timestamp ?? '0') < GROUP_MS);
    rows.push(<PendingRow key={p.nonce} p={p} cont={!!cont} resolver={resolver} />);
  });

  const start =
    inThread && cm?.loaded && !cm.hasMore && canHistory ? (
      <ThreadStart thread={channel} />
    ) : cm?.loaded && !cm.hasMore && !canHistory ? (
      <div className="ch-start ns-nohist" role="note">
        <div className="ch-start-ic">
          <Icon name="lock" size="xl" />
        </div>
        {/* the app's sentences take the UI's direction (the names in them are isolated) */}
        <h2>{t('ns.hist.noPerm', { name })}</h2>
        <p>{t('ns.hist.noPermSub')}</p>
      </div>
    ) : cm?.loaded && !cm.hasMore ? (
      <div className="ch-start">
        {/* the DM welcome (WP6): an 80px avatar in a 6px ring, the name, the username */}
        <div className={'ch-start-ic' + (isDm && dmPeer ? ' is-av hubv-dmw-av' : '')}>
          {isDm && dmPeer ? (
            <Avatar user={dmPeer} size={80} />
          ) : (
            <Icon name={voice ? 'volume' : isDm ? 'at' : 'hash'} size="xl" />
          )}
        </div>
        {/* a DM's title is the person's name (their own text); the rest are the app's sentences */}
        <h2 dir={isDm ? 'auto' : undefined}>
          {isDm ? title : t(voice ? 'ns.start.voiceTitle' : 'hub.start.title', { name: title })}
        </h2>
        {isDm && dmPeer && !isDeletedUser(dmPeer) ? (
          <p className="hubv-dmw-un" dir="ltr">
            {dmPeer.username}
          </p>
        ) : null}
        <p>
          {isDm
            ? t('ns.dm.start', { name: title })
            : t(voice ? 'ns.start.voiceSub' : 'hub.start.sub', { name: title })}
        </p>
      </div>
    ) : null;

  // the jump-to-present bar, attached to the composer's top (§7.12)
  const jumpBar =
    far || viewingOlder ? (
      <div className="cmp-bar ns-older-bar" role="status">
        <span>{t('ns.hist.older')}</span>
        <button type="button" onClick={toPresent}>
          {t('ns.hist.jumpPresent')}
        </button>
      </div>
    ) : null;
  // "12 new since 21:44 · Mark read", under the header while the NEW line is above (§7.12)
  const newBar =
    firstNew && newAbove && !acked ? (
      <div className="ns-new-bar" role="status">
        <button
          type="button"
          className="ns-new-go"
          onClick={() =>
            scroller.current
              ?.querySelector('.nx-newline')
              ?.scrollIntoView({ block: 'center', behavior: motionOk() ? 'smooth' : 'auto' })
          }
        >
          {t(
            cm?.hasMore && markId && compareSnowflakes(list[0]?.id ?? '0', markId) > 0
              ? 'chat.new.countMore'
              : 'chat.new.count',
            {
              n: newCount,
              time: fmt.time(firstNew.timestamp),
            },
          )}
        </button>
        <button
          type="button"
          className="ns-new-read"
          onClick={() => {
            ackChannel(channelId);
            setMark({ ch: channelId, id: null });
          }}
        >
          {t('chat.new.markRead')}
          <Icon name="check" size="s" />
        </button>
      </div>
    ) : null;

  const placeholder = t(
    inThread ? 'th.ph' : isDm ? 'ns.cmp.phDm' : voice ? 'ns.cmp.phVoice' : 'hub.cmp.ph',
    { name: cut(title) },
  );
  // a locked thread says why only moderators write there
  const threadLocked = inThread && !!channel.thread_metadata?.locked && !sendable;

  return (
    <section className={'hub-pane nx-panel' + (embedded ? ' ns-embedded' : '')} aria-label={name}>
      {!embedded ? (
        <header className="ch-head">
          <div
            className="ch-title"
            onContextMenu={channel.guild_id ? (e) => openTitleNotifyMenu(e, channel) : undefined}
          >
            {isDm && dmPeer ? (
              <Avatar user={dmPeer} size={24} presence={dmPresence} />
            ) : (
              <span className="ch-title-i" aria-hidden="true">
                <Icon name={isDm ? 'users' : voice ? 'volume' : 'hash'} size="m" />
              </span>
            )}
            <h1 dir="auto">{title}</h1>
            {isDm && dmPresence ? (
              <span className="hubv-dm-pres">{t(`pres.${dmPresence}`)}</span>
            ) : null}
            {channel.topic ? (
              <>
                <span className="ch-sep" aria-hidden="true" />
                <button
                  type="button"
                  className="ch-topic"
                  aria-haspopup="dialog"
                  data-tip={t('ns.topic.open')}
                  data-tip-pos="bottom"
                  onClick={() => setTopicOpen(true)}
                >
                  <span dir="auto">{plainText(channel.topic, resolver)}</span>
                </button>
                {topicOpen ? (
                  <Dialog
                    title={t('ns.topic.title')}
                    icon="hash"
                    cls="ns-topic-dlg"
                    onClose={() => setTopicOpen(false)}
                  >
                    <p className="ns-topic-ch" dir="auto">
                      {name}
                    </p>
                    <p className="ns-topic-full" dir="auto">
                      {renderTokens(tokenize(channel.topic), resolver, t('hub.spoiler'))}
                    </p>
                  </Dialog>
                ) : null}
              </>
            ) : null}
          </div>
          <div className="ch-acts">
            {channel.guild_id ? (
              <HeaderSearch
                value={query}
                onChange={setQuery}
                open={slot === 'search'}
                onClose={closeSlot}
                enabled={searchOn}
              />
            ) : null}
            <ThreadsHeaderButton channel={channel} />
            {/* Mute and the levels (notification settings, 2026-10-29) */}
            <NotifyBell channel={channel} />
            <IconBtn
              icon="pin"
              className="ch-pins"
              label={pinsOn ? t('hub.h.pins') : t('cfg.off.pins')}
              aria-disabled={!pinsOn || undefined}
              tipPos="bottom"
              pressed={slot === 'pins'}
              onClick={() =>
                pinsOn
                  ? setSlot((s) => (s === 'pins' ? null : 'pins'))
                  : toast(t('cfg.off.pins'), { icon: 'pin', tone: 'warn' })
              }
            />
            {/* a DM's own actions after Pins (WP6): a group's Add, Members, More; a DM's profile */}
            {isDm ? <DmHeaderActions channel={channel} compact={lay.layout === 'phone'} /> : null}
            {!isDm ? (
              <IconBtn
                icon="users"
                className="ch-members"
                label={t(membersOn && !slot ? 'hub.h.hideMembers' : 'hub.h.members')}
                tipPos="bottom"
                pressed={membersOn && !slot}
                onClick={toggleMembers}
              />
            ) : null}
          </div>
        </header>
      ) : null}
      {/* a DM's call, under its header (DM calls, 2026-11-01) */}
      {isDm && !embedded ? <DmCallBand channelId={channelId} /> : null}
      <div
        className={
          'ch-body' +
          (panel ? ' with-side' : '') +
          (panel && panel !== 'members' ? ' with-panel' : '') +
          (panel === 'thread' ? ' ns-with-thread' : '')
        }
      >
        <div className="chat" onKeyDown={onChatKey}>
          {newBar}
          <div
            className="msgs nx-scroll-read"
            ref={scroller}
            role="log"
            aria-label={t(isDm || voice ? 'ns.a11y.messagesIn' : 'hub.a11y.messages', {
              name: title,
            })}
            tabIndex={-1}
            onScroll={onScroll}
            onKeyDown={onListKey}
            onFocus={(e) => {
              const m = (e.target as HTMLElement).closest<HTMLElement>('[data-mid]');
              if (m) active.current = m.id;
            }}
          >
            <div className="msgs-in" ref={inner}>
              {loading === 'older' ? (
                <div className="ns-loading-row">
                  <i className="nx-spin" /> {t('ns.loadingOlder')}
                </div>
              ) : null}
              {start}
              {loading === 'initial' && !list.length ? (
                <div className="ns-loading-row">
                  <i className="nx-spin" /> {t('ns.loading')}
                </div>
              ) : null}
              {failed ? (
                <div className="ns-loading-row" role="alert">
                  <Icon name="alert" size="s" /> {t('ns.msg.historyFailed')}{' '}
                  <Btn
                    kind="ghost"
                    size="xs"
                    onClick={() => void loadHistory(channelId, !!cm?.loaded)}
                  >
                    {t('ns.msg.retry')}
                  </Btn>
                </div>
              ) : null}
              {rows}
            </div>
          </div>
          {dmBlocked && dmPeer ? (
            <div className="cmp-wrap">
              {jumpBar}
              <div className="cmp ns-cmp-locked ns-dm-blocked" role="status">
                <Icon name="shield" size="s" />
                {/* the sentence takes the UI's direction, not the name's */}
                <span>{t('social.block.dmBar', { name: title })}</span>
                <Btn kind="ghost" size="xs" onClick={() => void unblock(dmPeer.id, title)}>
                  {t('block.unblock')}
                </Btn>
              </div>
            </div>
          ) : threadLocked ? (
            <ThreadLockedNote />
          ) : (
            <Composer
              channel={channel}
              name={name}
              placeholder={placeholder}
              replyTo={replyTo}
              onCancelReply={() => setReplyTo(null)}
              canSend={sendable}
              canAttach={uploadsOn && can(perms, Permissions.ATTACH_FILES)}
              canEveryone={canEveryone}
              // an archived thread: a message brings it back (Discord)
              topBar={jumpBar ?? (inThread ? <ThreadArchivedNote thread={channel} /> : null)}
              onSent={onSent}
            />
          )}
          <Typing channelId={channelId} guildId={channel.guild_id} />
        </div>
        {/* phones and tablets: the drawer's scrim; a tap on it closes the drawer */}
        {layer ? <div className="ch-scrim" aria-hidden="true" onClick={closeLayer} /> : null}
        {panel ? (
          <aside
            className={'hub-side-slot' + (panel === 'members' ? ' members-slot' : ' panel-slot')}
          >
            {panel === 'members' ? (
              <MemberList
                guildId={channel.guild_id ?? activeGuildId(useData.getState()) ?? ''}
                channel={channel}
              />
            ) : panel === 'search' && channel.guild_id ? (
              <SearchPanel
                guildId={channel.guild_id}
                close={closeSlot}
                query={query}
                onQuery={setQuery}
              />
            ) : panel === 'pins' ? (
              <Pins channelId={channelId} ctx={ctx} close={closeSlot} />
            ) : panel === 'thread' && threadId ? (
              <ThreadPanel threadId={threadId}>
                <ChannelView key={threadId} channelId={threadId} embedded inThread />
              </ThreadPanel>
            ) : panel === 'threads' && threadsPanel ? (
              <ThreadList channel={channel} panel={threadsPanel} />
            ) : null}
          </aside>
        ) : null}
      </div>
    </section>
  );
}

export { sortedRoles, compareSnowflakes };
